/**
 * ExistingDebtHeader - Header Section for Existing Debt Inputs
 *
 * Contains:
 * - Heading with quick fill buttons and verification button
 * - Debt name input
 * - Final payment date selector
 * - Generate schedule button
 */

import {
  Box,
  Button,
  createListCollection,
  Heading,
  HStack,
  Portal,
  Select,
  SimpleGrid,
} from '@chakra-ui/react';
import React from 'react';

import { TextInput } from '~/components/inputs';
import type { ExistingDebtInputForm } from '~/types';

interface ExistingDebtHeaderProps {
  values: ExistingDebtInputForm;
  finalPaymentDate: string;
  setFinalPaymentDate: (date: string) => void;
  paymentDateSearchQuery: string;
  setPaymentDateSearchQuery: (query: string) => void;
  filteredPaymentDateOptions: Array<{ value: string; label: string }>;
  isDisabled: boolean;
  onNameChange: (value: string) => void;
  onGenerateSchedule: () => void;
}

export const ExistingDebtHeader: React.FC<ExistingDebtHeaderProps> = ({
  values,
  finalPaymentDate,
  setFinalPaymentDate,
  paymentDateSearchQuery,
  setPaymentDateSearchQuery,
  filteredPaymentDateOptions,
  isDisabled,
  onNameChange,
  onGenerateSchedule,
}) => {
  // Create collection for Select component
  const paymentDateCollection = React.useMemo(() => {
    return createListCollection({
      items: filteredPaymentDateOptions,
    });
  }, [filteredPaymentDateOptions]);

  return (
    <>
      {/* Simple heading - no collapse */}
      <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
        <Heading>Existing Debt Information</Heading>
      </HStack>

      {/* Basic Debt Information - Single Row */}
      <Box>
        <SimpleGrid columns={3} gap={3} alignItems="flex-end">
          <TextInput
            label="Debt Name"
            size="sm"
            value={values.name}
            onChange={onNameChange}
            placeholder="e.g., 2020 Bond Series"
          />
          <Box>
            <Select.Root
              collection={paymentDateCollection}
              value={finalPaymentDate ? [finalPaymentDate] : []}
              onValueChange={(details: { value: string[] }) => {
                const selectedValue = details.value[0] || '';
                setFinalPaymentDate(selectedValue);
                setPaymentDateSearchQuery('');
              }}
              size="sm"
              positioning={{ sameWidth: true }}
              disabled={isDisabled}
              onOpenChange={(details: { open: boolean }) => {
                if (!details.open) {
                  setPaymentDateSearchQuery('');
                }
              }}
            >
              <Select.HiddenSelect />
              <Select.Label>Final Payment Date</Select.Label>
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText placeholder="Select payment date" />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    <Box px={2} py={1}>
                      <TextInput
                        size="sm"
                        placeholder="Type to search (e.g., 2030)"
                        value={paymentDateSearchQuery}
                        onChange={(value: string) => setPaymentDateSearchQuery(value)}
                      />
                    </Box>
                    <Select.ItemGroup id="payment-dates">
                      {filteredPaymentDateOptions.map((option) => (
                        <Select.Item item={option} key={option.value}>
                          {option.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                      {filteredPaymentDateOptions.length === 0 && (
                        <Box px={2} py={2} fontSize="sm" color="gray.500">
                          No dates found matching "{paymentDateSearchQuery}"
                        </Box>
                      )}
                    </Select.ItemGroup>
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
          </Box>
          <Button
            onClick={onGenerateSchedule}
            colorPalette="blue"
            size="sm"
            disabled={!finalPaymentDate || !values.name}
          >
            Generate Schedule
          </Button>
        </SimpleGrid>
      </Box>
    </>
  );
};
